import React, { useState } from 'react';

const content = {
  en: {
    toggleLabel: '中文',
    title: 'Privacy Policy',
    subtitle: 'Vessel Application',
    disclaimer:
      'This is a personal mood-journaling application built for self-reflection and personal growth — a quiet space to get to know yourself a little better. Everyone experiences highs and lows, and recording how you feel does not mean you are unwell or have a mental-health condition. If you are experiencing emotional or psychological difficulties, please consult a licensed mental-health professional. Vessel is not a medical device and is not a substitute for professional medical advice, diagnosis, or treatment.',
    intro:
      'Vessel ("we", "our", "us") is built around a single principle: your data stays with you. Vessel is a frontend-only iOS application — there are no servers, no accounts, and no remote backends operated by us. Everything you write, draw, record, or score in Vessel is stored locally on your device, and only you have access to it.',
    introBoldPrefix: 'your data stays with you',
    s1Title: '1. Information We Do Not Collect',
    s1Body:
      'Vessel does not collect, transmit, or store any of the following on any external server:',
    s1List: [
      'Personal information (name, email, phone number, account identifiers)',
      'Mood scores, journal text, sketches, audio recordings, or video clips',
      'Device identifiers, advertising IDs, or location data',
      'Usage analytics, telemetry, or behavioral data',
      "Crash reports collected by us (any system-level diagnostics shared with Apple are governed by Apple, not by us)",
    ],
    s2Title: '2. Data Stored Locally on Your Device',
    s2Body:
      "The following information is stored only on your device, inside iOS's standard application sandbox, and never leaves your device:",
    s2List: [
      'Daily mood scores (morning and afternoon entries)',
      'Personal notes and journal entries',
      'Sketches, voice memos, or short videos created in Heart Trail',
      'Calendar history, charts, and your in-app preferences',
    ],
    s2Footer:
      'This data is never synced to any cloud we operate, never transmitted to us or any third party, and remains fully under your control at all times.',
    s3Title: '3. The Backup Function',
    s3Body1:
      "Vessel includes an in-app Backup button. When you tap Backup, the app exports a snapshot of your data as a local JSON-formatted file, written to storage on your own device. This backup file is created and stored entirely on the local device — it is not uploaded, synced, or transmitted to any server we operate.",
    s3Body2Bold: 'The Backup function does not guarantee against data loss.',
    s3Body2:
      " It is a frontend-only convenience feature. You are solely responsible for managing the backup file (for example, copying it to your own iCloud Drive, your computer, or another secure location). We do not retain any copy of your backup, and we cannot recover your data if it is lost, corrupted, or deleted.",
    s4Title: '4. No Account, No Login, No Network Calls',
    s4Body:
      'Vessel does not require an account or sign-in. The app does not make outbound network requests to any server we operate. Vessel functions entirely offline.',
    s5Title: '5. Apple System Services',
    s5Body:
      "Vessel runs on iOS and may rely on standard Apple-provided services (for example, Apple's diagnostic and crash reporting that you may opt into via your iPhone's privacy settings, or iCloud Backup if you choose to back up your device). Any data shared through those services is governed by Apple's own privacy policy, not by us. We do not receive that data.",
    s6Title: '6. No Third-Party SDKs or Trackers',
    s6Body:
      'Vessel does not include third-party advertising SDKs, analytics SDKs, or trackers. There are no advertising identifiers used or shared, and no cross-app tracking takes place.',
    s7Title: "7. Children's Privacy",
    s7Body:
      'Because Vessel does not collect any personal data, it does not knowingly collect data from anyone — including children under 13.',
    s8Title: '8. Your Choices and Control',
    s8Body: 'You are in full control of your own data:',
    s8List: [
      'Delete individual entries inside the app',
      "Reset all app data via the app's settings",
      'Uninstall the app to remove all stored data from your device',
    ],
    s8Footer:
      'Because we never receive a copy of your data, there is no recovery mechanism on our side. This is intentional, in keeping with our privacy-first design.',
    s9Title: '9. Data Retention & Security',
    s9Body:
      'Because no data is collected by us, we retain none of your data. Data on your device is protected by the standard security mechanisms provided by iOS, including device-level encryption when a passcode is set on the device.',
    s10Title: '10. Policy Changes',
    s10Body:
      'If a future version of Vessel introduces optional features that require any new form of data handling (for example, optional iCloud sync or a server-side backup feature), this Privacy Policy will be updated, and the change will be clearly disclosed inside the app before it takes effect. Continued use of the app after such an update indicates acceptance of the revised policy.',
    s11Title: '11. Contact',
    s11Body: 'For privacy questions, please email:',
    footer: 'Document effective as of the date of last revision.',
  },
  zh: {
    toggleLabel: 'EN',
    title: '私隱政策',
    subtitle: 'Vessel 應用程式',
    disclaimer:
      '本應用程式為個人心情紀錄工具，旨在協助你進行自我反思與個人成長 —— 一個安靜的空間，讓你更認識自己。每個人都會有情緒的高低起伏，紀錄心情並不代表你有病或心理出問題。如你正面對情緒或精神上的困擾，請務必諮詢持牌的精神健康專業人士。Vessel 並非醫療裝置，亦不能取代任何專業醫療建議、診斷或治療。',
    intro:
      'Vessel（下稱「我們」）建基於一個簡單原則：你的資料只屬於你。Vessel 是一個純前端的 iOS 應用程式 —— 我們沒有伺服器、沒有帳戶系統，亦沒有任何由我們營運的後端。你在 Vessel 中輸入、繪畫、錄製或評分的一切內容，都只儲存在你的裝置上，只有你能存取。',
    introBoldPrefix: '你的資料只屬於你',
    s1Title: '一、我們不會收集的資料',
    s1Body: 'Vessel 不會在任何外部伺服器上收集、傳輸或儲存以下任何資料：',
    s1List: [
      '個人資料（姓名、電郵、電話、帳戶識別碼）',
      '心情分數、日記文字、塗鴉、錄音或短片',
      '裝置識別碼、廣告 ID 或位置資料',
      '使用分析、遙測或行為數據',
      '由我們收集的當機報告（你透過 iPhone 設定主動分享給 Apple 的系統層診斷資料由 Apple 規範，與我們無關）',
    ],
    s2Title: '二、儲存於你裝置本機的資料',
    s2Body:
      '以下資料只會儲存在你的裝置中，置於 iOS 標準應用程式沙盒內，永不離開你的裝置：',
    s2List: [
      '每日心情分數（上午與下午）',
      '個人筆記與日記內容',
      '在「心跡」中建立的塗鴉、語音備忘或短片',
      '日曆紀錄、圖表，以及應用程式內的個人偏好',
    ],
    s2Footer:
      '這些資料絕不會同步到任何由我們營運的雲端，亦不會傳送給我們或任何第三方，永遠完全由你掌控。',
    s3Title: '三、備份功能',
    s3Body1:
      'Vessel 應用程式內置「備份」按鈕。當你點擊備份時，應用程式會以 JSON 格式將你的資料匯出為一個本機快照檔，並寫入你裝置上的儲存空間。此備份檔完全在你的裝置本機建立及儲存 —— 不會上傳、同步或傳送至任何由我們營運的伺服器。',
    s3Body2Bold: '備份功能並不保證資料不會遺失。',
    s3Body2:
      '它只是一個前端的便利功能。你須自行妥善管理該備份檔（例如自行複製到你的 iCloud Drive、電腦或其他安全位置）。我們不會保留你備份檔的任何副本，亦無法在資料遺失、損壞或被刪除後協助你恢復資料。',
    s4Title: '四、無帳戶、無登入、無網絡請求',
    s4Body:
      'Vessel 不需要建立帳戶或登入。應用程式不會向任何由我們營運的伺服器發出網絡請求，可完全離線運作。',
    s5Title: '五、Apple 系統服務',
    s5Body:
      'Vessel 在 iOS 上執行，可能會依賴 Apple 所提供的標準系統服務（例如你在 iPhone 私隱設定中選擇分享給 Apple 的診斷與當機報告，或者你選擇使用的 iCloud 備份）。透過這些服務分享的任何資料，皆由 Apple 自身的私隱政策規範，與我們無關。我們不會收到該等資料。',
    s6Title: '六、不使用第三方 SDK 或追蹤工具',
    s6Body:
      'Vessel 沒有整合任何第三方廣告 SDK、分析 SDK 或追蹤工具。應用程式不會使用或分享任何廣告識別碼，亦不會進行跨應用程式追蹤。',
    s7Title: '七、兒童私隱',
    s7Body:
      '由於 Vessel 不收集任何個人資料，因此亦不會知情地收集任何人的資料 —— 包括 13 歲以下兒童。',
    s8Title: '八、你的選擇與控制權',
    s8Body: '你的資料完全由你掌控：',
    s8List: [
      '在應用程式內刪除個別紀錄',
      '透過應用程式設定，重設所有資料',
      '解除安裝應用程式，從裝置中移除所有已儲存的資料',
    ],
    s8Footer:
      '由於我們從不持有你的資料副本，因此我們這邊並沒有任何資料恢復機制。這是有意的設計，與我們「私隱優先」的理念一致。',
    s9Title: '九、資料保存與安全',
    s9Body:
      '由於我們不收集任何資料，因此我們亦不保留任何你的資料。儲存於你裝置上的資料受 iOS 標準的安全機制保護，當裝置設有密碼鎖定時，更會受裝置層級加密保護。',
    s10Title: '十、政策變更',
    s10Body:
      '若 Vessel 日後推出需要全新資料處理方式的選用功能（例如可選的 iCloud 同步或伺服器端備份），本私隱政策將會更新，並會在生效前於應用程式內清楚告知。如你在更新後繼續使用應用程式，即視為接受修訂後的政策。',
    s11Title: '十一、聯絡我們',
    s11Body: '如有私隱相關問題，請電郵至：',
    footer: '本文件以最新修訂日期為生效日期。',
  },
};

export default function VesselPrivacy() {
  const [lang, setLang] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    return localStorage.getItem('vesselDocLang') === 'zh' ? 'zh' : 'en';
  });
  const t = content[lang];

  const toggleLang = () => {
    const next = lang === 'en' ? 'zh' : 'en';
    setLang(next);
    if (typeof window !== 'undefined') {
      localStorage.setItem('vesselDocLang', next);
    }
  };

  const styles = {
    page: {
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      fontFamily: 'Times, "Times New Roman", serif',
      color: '#000000',
      margin: 0,
      padding: 0,
    },
    container: {
      maxWidth: '8.5in',
      margin: '0 auto',
      padding: '1in',
      lineHeight: '1.5',
      fontSize: '12pt',
    },
    langToggle: {
      position: 'fixed',
      top: '20px',
      right: '24px',
      background: '#ffffff',
      border: '1.5px solid #000',
      padding: '6px 14px',
      fontFamily: 'Times, "Times New Roman", serif',
      fontSize: '11pt',
      cursor: 'pointer',
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      fontWeight: 'bold',
      color: '#000',
      zIndex: 100,
      boxShadow: '2px 2px 0 #000',
    },
    header: {
      textAlign: 'center',
      marginBottom: '30px',
      borderBottom: '2px solid #000',
      paddingBottom: '20px',
    },
    title: {
      fontSize: '18pt',
      fontWeight: 'bold',
      margin: '0 0 10px 0',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    subtitle: {
      fontSize: '14pt',
      fontWeight: 'normal',
      margin: '0',
      fontStyle: 'italic',
    },
    disclaimer: {
      margin: '0 0 30px 0',
      padding: '18px 22px',
      border: '1.5px solid #000',
      backgroundColor: '#fafafa',
    },
    disclaimerText: {
      margin: 0,
      fontSize: '11.5pt',
      lineHeight: '1.6',
      textAlign: 'justify',
      fontWeight: 'bold',
    },
    section: {
      marginBottom: '25px',
      pageBreakInside: 'avoid',
    },
    sectionNumber: {
      fontSize: '14pt',
      fontWeight: 'bold',
      marginBottom: '10px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    paragraph: {
      fontSize: '12pt',
      marginBottom: '12px',
      textAlign: 'justify',
      textIndent: lang === 'zh' ? '0' : '0.5in',
      lineHeight: '1.5',
    },
    paragraphNoIndent: {
      fontSize: '12pt',
      marginBottom: '12px',
      textAlign: 'justify',
      lineHeight: '1.5',
    },
    list: {
      paddingLeft: '0.75in',
      marginBottom: '12px',
    },
    listItem: {
      fontSize: '12pt',
      marginBottom: '6px',
      listStyleType: 'disc',
    },
    footer: {
      marginTop: '40px',
      paddingTop: '20px',
      borderTop: '1px solid #000',
      textAlign: 'center',
      fontSize: '10pt',
      color: '#666',
    },
  };

  return (
    <div style={styles.page} lang={lang === 'zh' ? 'zh-Hant' : 'en'}>
      <button
        type="button"
        onClick={toggleLang}
        style={styles.langToggle}
        aria-label={lang === 'en' ? 'Switch to Chinese' : 'Switch to English'}
      >
        {t.toggleLabel}
      </button>

      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>{t.title}</h1>
          <p style={styles.subtitle}>{t.subtitle}</p>
        </div>

        <div style={styles.disclaimer}>
          <p style={styles.disclaimerText}>{t.disclaimer}</p>
        </div>

        <div style={styles.section}>
          <p style={styles.paragraphNoIndent}>{t.intro}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s1Title}</h2>
          <p style={styles.paragraph}>{t.s1Body}</p>
          <ul style={styles.list}>
            {t.s1List.map((item, i) => (
              <li key={i} style={styles.listItem}>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s2Title}</h2>
          <p style={styles.paragraph}>{t.s2Body}</p>
          <ul style={styles.list}>
            {t.s2List.map((item, i) => (
              <li key={i} style={styles.listItem}>
                {item}
              </li>
            ))}
          </ul>
          <p style={styles.paragraphNoIndent}>{t.s2Footer}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s3Title}</h2>
          <p style={styles.paragraphNoIndent}>{t.s3Body1}</p>
          <p style={styles.paragraphNoIndent}>
            <strong>{t.s3Body2Bold}</strong>
            {t.s3Body2}
          </p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s4Title}</h2>
          <p style={styles.paragraph}>{t.s4Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s5Title}</h2>
          <p style={styles.paragraph}>{t.s5Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s6Title}</h2>
          <p style={styles.paragraph}>{t.s6Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s7Title}</h2>
          <p style={styles.paragraph}>{t.s7Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s8Title}</h2>
          <p style={styles.paragraph}>{t.s8Body}</p>
          <ul style={styles.list}>
            {t.s8List.map((item, i) => (
              <li key={i} style={styles.listItem}>
                {item}
              </li>
            ))}
          </ul>
          <p style={styles.paragraphNoIndent}>{t.s8Footer}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s9Title}</h2>
          <p style={styles.paragraph}>{t.s9Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s10Title}</h2>
          <p style={styles.paragraph}>{t.s10Body}</p>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionNumber}>{t.s11Title}</h2>
          <p style={styles.paragraphNoIndent}>
            {t.s11Body} <strong>flowintothewall@gmail.com</strong>
          </p>
        </div>

        <div style={styles.footer}>
          <p>{t.footer}</p>
        </div>
      </div>
    </div>
  );
}
